import React, { useLayoutEffect, useRef, useState } from "react";
import { Navigation, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

gsap.registerPlugin(ScrollTrigger);

const customers = [
  { img: "/images/customers/cafe.png", title: "کافه", emoji: "☕", text: "منوی QR برای هر میز" },
  { img: "/images/customers/sweet.png", title: "قنادی", emoji: "🍰", text: "ویترین آنلاین کیک و شیرینی" },
  { img: "/images/customers/foodcourt.png", title: "فودکورت", emoji: "🍔", text: "چند غرفه، یک منوی یکپارچه" },
  { img: "/images/customers/hotel.png", title: "هتل", emoji: "🏨", text: "رستوران و روم‌سرویس در یک پنل" },
  { img: "/images/customers/ice-cream.png", title: "بستنی فروشی", emoji: "🍦", text: "طعم‌ها و قیمت‌ها همیشه به‌روز" },
  { img: "/images/customers/restaurant.png", title: "رستوران", emoji: "🍽️", text: "منو، رزرو میز و مدیریت سالن" },
  { img: "/images/customers/traditional.png", title: "سنتی", emoji: "🫖", text: "حس سنتی، تجربه دیجیتال" },
  { img: "/images/customers/ketring.png", title: "کترینگ", emoji: "🍱", text: "منوی مناسبت‌ها و سفارش‌های گروهی" },
];

// Emojis drifting around the heading, as in the product story.
const FLOATING_EMOJIS = [
  { emoji: "🍦", className: "right-[8%] top-14 text-4xl", depth: 1.2 },
  { emoji: "🥐", className: "left-[10%] top-24 text-3xl", depth: 0.8 },
  { emoji: "🍕", className: "left-[4%] bottom-24 hidden text-4xl md:block", depth: 1.5 },
  { emoji: "🧁", className: "right-[3%] bottom-32 hidden text-3xl md:block", depth: 1 },
];

const toPersianDigits = (value) =>
  String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function CustomerSection() {
  const sectionRef = useRef(null);
  const progressRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [reducedMotion] = useState(prefersReducedMotion);

  // Motion lives here rather than in Tailwind classes on the same nodes:
  // this project's Tailwind classes are !important and would override the
  // inline transforms GSAP writes. GSAP animates the outer wrappers
  // ([data-customer-card], [data-card-media], [data-card-emoji]); CSS hover
  // effects run on their children.
  useLayoutEffect(() => {
    const root = sectionRef.current;
    if (!root) return undefined;

    const media = gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)", () => {
      const cards = root.querySelectorAll("[data-customer-card]");
      const badges = root.querySelectorAll("[data-card-emoji]");

      gsap
        .timeline({
          scrollTrigger: { trigger: root, start: "top 70%", toggleActions: "play none none none" },
        })
        .fromTo(
          root.querySelector("[data-customer-heading]"),
          { y: 30, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 0.7, ease: "power3.out" }
        )
        .fromTo(
          cards,
          { y: 90, autoAlpha: 0, rotate: (i) => (i % 2 ? 3 : -3) },
          {
            y: 0,
            autoAlpha: 1,
            rotate: 0,
            duration: 0.9,
            stagger: 0.12,
            ease: "power3.out",
          },
          "-=0.35"
        )
        .fromTo(
          badges,
          { scale: 0, rotate: -40 },
          {
            scale: 1,
            rotate: 0,
            duration: 0.5,
            stagger: 0.08,
            ease: "back.out(2.2)",
          },
          "-=0.5"
        );

      // Photos drift inside their cards while the section scrolls past.
      gsap.fromTo(
        root.querySelectorAll("[data-card-media]"),
        { yPercent: -5 },
        {
          yPercent: 5,
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        }
      );

      root.querySelectorAll("[data-floating-emoji]").forEach((emoji) => {
        const depth = Number(emoji.dataset.depth) || 1;
        gsap.fromTo(
          emoji,
          { y: 50 * depth, rotate: -8 * depth },
          {
            y: -70 * depth,
            rotate: 10 * depth,
            ease: "none",
            scrollTrigger: {
              trigger: root,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      });
    });

    return () => media.revert();
  }, []);

  const handleTimeLeft = (_swiper, _time, progress) => {
    if (progressRef.current) {
      progressRef.current.style.width = `${(1 - progress) * 100}%`;
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden border-y border-[#e3eeea] bg-[#f3f9f7] py-16"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {FLOATING_EMOJIS.map(({ emoji, className, depth }) => (
          <span
            key={emoji}
            data-floating-emoji
            data-depth={depth}
            className={`absolute select-none leading-none ${className}`}
          >
            {emoji}
          </span>
        ))}
      </div>

      <div className="relative mx-auto max-w-6xl px-4 text-center">
        <div data-customer-heading>
          <SectionHeading
            eyebrow="برای همه"
            title="نیاز هر نوع مجموعه غذایی"
            highlight="هر نوع"
          />
        </div>

        <Swiper
          modules={[Navigation, Autoplay]}
          navigation={{
            nextEl: ".swiper-next",
            prevEl: ".swiper-prev",
          }}
          autoplay={
            reducedMotion
              ? false
              : { delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }
          }
          speed={reducedMotion ? 0 : 900}
          loop={true}
          spaceBetween={24}
          slidesPerView={4}
          breakpoints={{
            0: { slidesPerView: 1.15 },
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 4 },
          }}
          onAutoplayTimeLeft={handleTimeLeft}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          className="pb-6 pt-3"
          style={{
            "--swiper-wrapper-transition-timing-function":
              "cubic-bezier(.22,1,.36,1)",
          }}
        >
          {customers.map((item) => (
            <SwiperSlide key={item.title}>
              <div data-customer-card>
                <div
                  tabIndex={0}
                  className="group relative block aspect-[3/4] overflow-hidden rounded-3xl bg-[#102b23] text-right shadow-[0_14px_34px_rgba(16,43,35,.14)] outline-none transition duration-500 ease-out focus-visible:ring-4 focus-visible:ring-primary/40 motion-safe:hover:-translate-y-2 hover:shadow-[0_24px_48px_rgba(16,43,35,.24)]"
                >
                  <div
                    data-card-media
                    className="absolute inset-x-0 -bottom-[6%] -top-[6%]"
                  >
                    <img
                      src={item.img}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out motion-safe:group-hover:scale-110 motion-safe:group-focus-visible:scale-110"
                      alt=""
                    />
                  </div>

                  {/* Dark fade for label contrast; it deepens on hover so
                      the revealed description stays readable. */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent transition-opacity duration-500 md:opacity-80 md:group-hover:opacity-100 md:group-focus-visible:opacity-100"
                  />

                  <span
                    data-card-emoji
                    className="absolute left-4 top-4"
                    aria-hidden="true"
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-white text-xl shadow-md transition-transform duration-500 motion-safe:group-hover:rotate-12 motion-safe:group-hover:scale-110">
                      {item.emoji}
                    </span>
                  </span>

                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <h3 className="text-xl font-black text-white">{item.title}</h3>
                    <span
                      aria-hidden="true"
                      className="mt-2 block h-1 w-8 rounded-full bg-primary transition-all duration-500 group-hover:w-16 group-focus-visible:w-16"
                    />
                    <p className="mt-2 overflow-hidden text-sm leading-6 text-white/90 transition-all duration-500 md:max-h-0 md:opacity-0 md:group-hover:max-h-12 md:group-hover:opacity-100 md:group-focus-visible:max-h-12 md:group-focus-visible:opacity-100">
                      {item.text}
                    </p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Counter, autoplay progress and navigation */}
        <div className="mt-4 flex items-center gap-4 px-1">
          <span className="text-sm font-black text-[#102b23]" aria-hidden="true">
            {/* <bdi> keeps each number whole so the pair reads "current / total"
                in right-to-left order instead of being reordered as one run. */}
            <bdi>{toPersianDigits(String(activeIndex + 1).padStart(2, "0"))}</bdi>
            <span className="mx-1 text-[#4a5a54]/60">/</span>
            <bdi className="text-[#4a5a54]">
              {toPersianDigits(String(customers.length).padStart(2, "0"))}
            </bdi>
          </span>
          <span className="h-1 flex-1 overflow-hidden rounded-full bg-[#102b23]/10" aria-hidden="true">
            <span
              ref={progressRef}
              className="block h-full rounded-full bg-primary"
              style={{ width: reducedMotion ? "100%" : "0%" }}
            />
          </span>
          <div className="flex gap-3">
            <button type="button" aria-label="اسلاید قبلی" className="swiper-prev group flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-primary focus:outline-none focus:ring-4 focus:ring-primary/30">
              <ChevronRight size={22} aria-hidden="true" className="transition-transform motion-safe:group-hover:translate-x-0.5" />
            </button>
            <button type="button" aria-label="اسلاید بعدی" className="swiper-next group flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-primary focus:outline-none focus:ring-4 focus:ring-primary/30">
              <ChevronLeft size={22} aria-hidden="true" className="transition-transform motion-safe:group-hover:-translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
